"use client";

import type { ReactNode } from "react";
import { useLiveActivityCommands } from "@/hooks/use-live-activity-commands";
import { useSettings } from "@/hooks/use-weight-training";
import {
  useWorkoutSessionController,
  WorkoutSessionContext,
} from "@/hooks/use-workout-session";
import { workoutNativeSessionId } from "@/lib/native/workout-identity";

/** Keeps native controls and recovery alive when the logger route is closed. */
export function WorkoutSessionProvider({ children }: { children: ReactNode }) {
  const { data: settings } = useSettings();
  const session = useWorkoutSessionController({
    weightUnit: settings?.weightUnit ?? "kg",
    defaultRestSeconds: settings?.defaultRestSeconds ?? 90,
    // The logger starts rest after its completion animation.
    timerAutoStart: false,
  });

  useLiveActivityCommands(
    {
      onWorkoutCompleteSet: (setId) => {
        if (setId) session.completeSetById(setId);
      },
      onWorkoutSkipRest: session.cancelRestTimer,
      onWorkoutAddRest: (seconds) => session.adjustRestTimer(seconds),
      onWorkoutSubtractRest: (seconds) => session.adjustRestTimer(-seconds),
      onWorkoutEnd: session.finishWorkout,
    },
    {
      sessionId:
        session.session.accountId && session.workoutId
          ? workoutNativeSessionId(session.session.accountId, session.workoutId)
          : undefined,
      hasAppliedCommand: session.hasAppliedCommand,
    },
  );

  return (
    <WorkoutSessionContext.Provider value={session}>
      {children}
    </WorkoutSessionContext.Provider>
  );
}
